import React from "react";
import { Flex, type FlexProps, SimpleGrid, Text } from "@chakra-ui/react";
import { MotionBox } from "ui/chakra-motion";

export type WorkflowStage = {
  id: string;
  title: string;
  tasks: Array<{
    id: string;
    label: string;
    highlighted?: boolean;
  }>;
};

export const workflowStages: WorkflowStage[] = [
  {
    id: "planning",
    title: "Planning",
    tasks: [
      { id: "campaign-brief", label: "Campaign brief" },
      { id: "stakeholder-alignment", label: "Stakeholder alignment" },
      { id: "creative-brief", label: "Creative brief" },
      { id: "experiment-plan", label: "Experiment plan" },
      { id: "delivery-logistics", label: "Delivery logistics" },
    ],
  },
  {
    id: "design",
    title: "Design",
    tasks: [
      { id: "copy-generation", label: "Copy generation" },
      { id: "image-generation", label: "Image generation" },
      { id: "asset-selection", label: "Asset selection" },
      { id: "email-design", label: "Email design" },
      { id: "personalization", label: "Personalization" },
    ],
  },
  {
    id: "approve",
    title: "Approve",
    tasks: [
      { id: "working-team-creative-approvals", label: "Working team creative approvals" },
      { id: "compliance-review", label: "Compliance review" },
      { id: "brand-review", label: "Brand review" },
      { id: "exec-review", label: "Exec review" },
      { id: "legal-review", label: "Legal review" },
    ],
  },
  {
    id: "build",
    title: "Build",
    tasks: [
      { id: "email", label: "Email" },
      { id: "in-app", label: "In-app" },
      { id: "sms", label: "SMS" },
      { id: "push", label: "Push" },
      { id: "social-ad", label: "Social ad" },
    ],
  },
  {
    id: "launch",
    title: "Launch",
    tasks: [
      { id: "audience-build", label: "Audience build" },
      { id: "sync-to-destination", label: "Sync to destination" },
      { id: "campaign-setup", label: "Campaign setup" },
      { id: "send-monitor", label: "Send & monitor" },
      { id: "split-testing", label: "Split testing" },
    ],
  },
  {
    id: "analyze",
    title: "Analyze",
    tasks: [
      { id: "testing-results", label: "Testing results" },
      { id: "campaign-performance", label: "Campaign performance" },
      { id: "cohort-analysis", label: "Cohort analysis" },
      { id: "creative-analysis", label: "Creative analysis" },
      { id: "plan-next-steps", label: "Plan next steps" },
    ],
  },
];

const sharedCardStyles: FlexProps = {
  align: "center",
  justify: "center",
  borderRadius: { base: "tinier", sm: "tiny" },
  border: "1px solid white",
  height: { base: "big", sm: "biggest" },
  px: "tiny",
  boxShadow: "sm",
};

const ANIMATION_DURATION = 0.6;
const STAGGER_DELAY = 0.08;
const headerCardBg = "#D0DBD5";

export function WorkflowChart() {
  return (
    <MotionBox initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }}>
      <SimpleGrid
        role="table"
        aria-label="Workflow stages and tasks"
        columns={6}
        gap={{ base: "tinier", md: "smallest" }}
        mt={{ base: "big", md: "48px" }}
        maxW="900px"
        mx="auto"
      >
        {workflowStages.map((stage) => (
          <Flex
            key={stage.id}
            role="columnheader"
            aria-label={`${stage.title} stage column`}
            direction="column"
            gap={{ base: "tinier", md: "smallest" }}
          >
            <MotionBox
              variants={{
                hidden: { opacity: 0, y: 20, scale: 0.95 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: ANIMATION_DURATION,
                    ease: [0.33, 1, 0.68, 1],
                  },
                },
              }}
            >
              <Flex backgroundColor={headerCardBg} {...sharedCardStyles}>
                <Text fontSize={{ base: "6px", sm: "10px", lg: "14px" }} fontWeight="bold" lineHeight={1.2}>
                  {stage.title}
                </Text>
              </Flex>
            </MotionBox>

            {stage.tasks.map((task, taskIndex) => (
              <MotionBox
                key={task.id}
                role="gridcell"
                aria-label={`${task.label} in ${stage.title} stage`}
                variants={{
                  hidden: { opacity: 0, y: 24, scale: 0.95 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: {
                      delay: taskIndex * STAGGER_DELAY,
                      duration: ANIMATION_DURATION,
                      ease: [0.33, 1, 0.68, 1],
                    },
                  },
                }}
              >
                <Flex backgroundColor="white" {...sharedCardStyles}>
                  <Text
                    fontSize={{ base: "6px", sm: "10px", lg: "14px" }}
                    fontWeight="medium"
                    color="new.gray.900"
                    textAlign="center"
                    lineHeight={1.2}
                  >
                    {task.label}
                  </Text>
                </Flex>
              </MotionBox>
            ))}
          </Flex>
        ))}
      </SimpleGrid>
    </MotionBox>
  );
}
